import { useState } from "react";

import { Box, Button, IconButton, Typography, CircularProgress, Chip } from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';

import { DialogBox, MetaDrivenTable, PaginationControls } from "../../../shared/components";
import { ExportDialog } from "../components";
import { useExport } from "../hooks/useExport";

interface ExportData {
    export_id: string;
    export_name: string;
    no_of_records: number;
    file_size: string;
    status: "READY" | "PENDING" | "FAILED";
    create_date: string;
    file_name: string;
    object_ref_id: string;
}

const STATUS_COLOR_MAP: Record<string, "default" | "warning" | "success" | "error"> = {
    READY: "success",
    PENDING: "warning",
    FAILED: "error",
};

export const Export = () => {
    const [exportDialogOpen, setExportDialogOpen] = useState(false);

    const {
        data,
        pagination,
        isLoading,
        isError,
        refetch,
        changePage,
        changeRowsPerPage,
        downloadingFileId,
        handleDownloadFile,
    } = useExport();

    const headers = [
        { alias_name: "export_name", label: "Export Name", display_order: 1, is_show: true },
        { alias_name: "no_of_records", label: "Records", display_order: 2, is_show: true },
        { alias_name: "file_size", label: "File Size", display_order: 3, is_show: true },
        { alias_name: "status", label: "Status", display_order: 4, is_show: true },
        { alias_name: "create_date", label: "Created", display_order: 5, is_show: true },
    ];

    const dataMetrics = {
        status: {
            type: "component" as const,
            element: (row: ExportData) => (
                <Chip
                    label={row.status}
                    color={STATUS_COLOR_MAP[row.status] || "default"}
                    size="small"
                    variant="outlined"
                />
            ),
        },
        create_date: {
            type: "date" as const,
        },
        no_of_records: {
            type: "component",
            element: ((row: ExportData) => (
                <Typography variant="body2">{row.no_of_records}</Typography>
            ))
        },
        file_size: {
            type: "component",
            element: ((row: ExportData) => {
                const fileSizeNum = parseFloat(row.file_size);
                const sizeInKB = fileSizeNum / 1024;
                const displaySize = sizeInKB < 1 ? `${fileSizeNum} KB` : `${(sizeInKB / 1024).toFixed(1)} MB`;
                return <Typography variant="body2">{displaySize}</Typography>;
            })
        },
    };

    const actions = [
        {
            isValidate: true,
            isHidden: (row: ExportData) => row.status === "READY" && !!row.object_ref_id,
            handleClick: (row: ExportData) => handleDownloadFile(row),
            isDisable: (row: ExportData) => downloadingFileId === row.object_ref_id,
            label: (row: ExportData) =>
                downloadingFileId === row.object_ref_id ? (
                    <CircularProgress size={20} />
                ) : (
                    <IconButton
                        size="small"
                        sx={{ color: "primary.main", p: 0.5 }}
                        aria-label={`Download ${row.file_name}`}
                    >
                        <DownloadIcon fontSize="small" />
                    </IconButton>
                ),
        },
    ];

    if (isLoading) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box display="flex" flexDirection="column" gap={2} py={2} height={'100%'}>
            <Box display="flex" justifyContent="flex-end" px={2}>
                <Button
                    variant="contained"
                    onClick={() => setExportDialogOpen(true)}
                    disabled={isLoading}
                >
                    Export Report
                </Button>
            </Box>

            {isError && (
                <Box px={2}>
                    <Typography color="error">Failed to load exports. Please try again.</Typography>
                </Box>
            )}

            <Box sx={{ flex: 1, overflow: "auto" }}>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <MetaDrivenTable
                        headers={headers}
                        data={data}
                        dataMetrics={dataMetrics}
                        sortDisabled={true}
                        actions={actions}
                    />
                )}
            </Box>

            <Box flexShrink={0}>
                <PaginationControls
                    total={pagination.total}
                    rowsPerPage={pagination.limit}
                    currentPage={pagination.page}
                    handlePageChange={changePage}
                    handleRowsPerPage={changeRowsPerPage}
                />
            </Box>

            <DialogBox
                showModal={exportDialogOpen}
                title="Export Report"
                subTitle=""
                onModalClose={() => setExportDialogOpen(false)}
                children={
                    <ExportDialog
                        onSuccess={() => {
                            setExportDialogOpen(false);
                            refetch();
                        }}
                    />
                }
                fullWidth
            />
        </Box>
    );
};